import {
  getLatestDataSourceInventory,
  getMaskedInventorySummary,
  inventoryContentOf,
  listMaskingJobs,
  readableJobError,
} from 'Secure/api';
import { getCoverageGaugeData } from 'Secure/Utils';
import { getMaskingHistogram, getAverageMaskingTimeMs } from 'Secure/SecureLandscapeUtils';
import { getHistory, getResultsPayloadForExport } from 'DataDiscovery/discoverApi';
import { findLatestSuccessfulRun } from 'Secure/secureDiscoverFields';
import { getFindingsSummary } from 'SensitiveDataLandscape/DataSourceRiskProfile/api';

const STATUS_LABELS = {
  PENDING: 'RUNNING',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'MASKED',
  FAILED: 'ERROR',
  CANCELLED: 'UNMASKED',
};

/** Map<lowerTableName, Set<lowerFieldName>> of the columns marked sensitive (masked) in the inventory. */
function maskedFieldSetFromInventory(inventory) {
  const map = new Map();
  const schemas = inventory && Array.isArray(inventory.schemas) ? inventory.schemas : [];
  schemas.forEach((schema) => {
    (schema.tables || []).forEach((table) => {
      const tName = table && table.name ? String(table.name).toLowerCase() : '';
      if (!tName) return;
      (table.columns || []).forEach((column) => {
        if (column && column.sensitive && column.name) {
          if (!map.has(tName)) map.set(tName, new Set());
          map.get(tName).add(String(column.name).toLowerCase());
        }
      });
    });
  });
  return map;
}

/** Discovery tables filtered down to only the masked (table, field) pairs, in getFindingsSummary shape. */
function maskedDiscoverTables(overviewTables, maskedMap) {
  const tables = [];
  (overviewTables || []).forEach((table) => {
    const tName = table && table.name ? String(table.name).toLowerCase() : '';
    const maskedFields = maskedMap.get(tName);
    if (!maskedFields || maskedFields.size === 0) return;
    const fields = (table.fields || []).filter(
      (f) => f && f.name && maskedFields.has(String(f.name).toLowerCase())
    );
    if (fields.length > 0) tables.push({ name: table.name, fields });
  });
  return tables;
}

/**
 * All data the Masking Summary page needs, mirroring DataSourceRiskProfile's fetch.
 * "What was masked" is derived from the saved inventory; the category breakdown joins those
 * masked fields against the latest discovery results (masking DTO carries neither).
 */
const getMaskingSummary = async (dataSourceId) => {
  const numericId = Number(dataSourceId);
  if (!Number.isFinite(numericId)) {
    throw new Error(`Invalid data source id "${dataSourceId}".`);
  }

  // Multi-inventory model: the summary reflects the source's most recently updated inventory.
  let inventory = null;
  try {
    inventory = inventoryContentOf(await getLatestDataSourceInventory(numericId));
  } catch (e) {
    inventory = null;
  }
  const invSummary = getMaskedInventorySummary(inventory);

  // Latest masking job → status label + headline stats (rows masked, duration, errors).
  let statusLabel = 'UNMASKED';
  let rowsMasked = null;
  let durationMs = null;
  let errorCount = null;
  let errorDetail = null;
  try {
    const jobsRes = await listMaskingJobs({ dataSourceId: numericId, page: 0, size: 1, sort: ['id,desc'] });
    const jobs = jobsRes && Array.isArray(jobsRes.content) ? jobsRes.content : [];
    if (jobs.length > 0) {
      const job = jobs[0];
      statusLabel = STATUS_LABELS[job.status] || 'UNMASKED';
      rowsMasked = job.recordsProcessed != null ? Number(job.recordsProcessed) : null;
      const startMs = Date.parse(job.startedAt);
      const finishMs = Date.parse(job.finishedAt);
      durationMs =
        Number.isFinite(startMs) && Number.isFinite(finishMs) && finishMs >= startMs
          ? finishMs - startMs
          : null;
      errorCount = job.status === 'FAILED' ? 1 : 0;
      if (job.status === 'FAILED') errorDetail = readableJobError(job);
    }
  } catch (e) {
    /* leave default */
  }

  // Latest successful discovery run → tables + searchid for the category breakdown / entity links.
  let searchid = '';
  let overviewTables = [];
  try {
    const historyRuns = await getHistory(0, 50, 'startedAt,desc', numericId);
    const latestRun = findLatestSuccessfulRun(historyRuns && historyRuns.runs ? historyRuns.runs : []);
    if (latestRun && latestRun.run_id) {
      searchid = String(latestRun.run_id);
      // Folder Search runs persist only the folder INDEX — reassemble the per-file
      // tables from chunks so masked-coverage categories reflect generic sources.
      const payload = (await getResultsPayloadForExport(searchid)) || {};
      overviewTables = Array.isArray(payload.tables) ? payload.tables : [];
    }
  } catch (e) {
    /* no discovery results — categories render empty */
  }

  const maskedMap = maskedFieldSetFromInventory(inventory);
  const filteredTables = maskedDiscoverTables(overviewTables, maskedMap);
  const summary = getFindingsSummary(filteredTables);

  const timeSeriesData = await getMaskingHistogram(numericId);
  const averageMaskingTimeMs = getAverageMaskingTimeMs(timeSeriesData);
  const coverage = getCoverageGaugeData(invSummary.maskedFields, invSummary.totalFields);

  return {
    ...invSummary,
    statusLabel,
    coveragePercent: coverage.percent,
    findings: summary.findingsValue,
    averageMaskingTimeMs,
    rowsMasked,
    durationMs,
    errorCount,
    errorDetail,
    timeSeriesData,
    searchid,
  };
};

export { getMaskingSummary };
